import {permiteRol} from '../../utils/permisosRol';
import { ESTADOS_QR } from '../../utils/estadosQr';
import { obtenerRelacionadoresEvento } from '../../utils/relacionadores';
import { resolverAtribucion, nombreAtribuido, etiquetaRelacionador } from '../../utils/identidadOperativa';
import { useAuth } from '../../context/AuthContext';
import { puedeAccederEvento } from '../../utils/accessPolicy';
// src/pages/admin/PanelInvitaciones.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { collection, query, deleteDoc, doc, updateDoc, where } from 'firebase/firestore';
import { getDocs, getDoc } from '../../utils/firestoreRead';
import { db } from '../../firebase/firebaseConfig';
import { exportCSV, exportPDF } from '../../utils/exportReport';
// Reutilizamos la estética de AdminEntradas
import '../entradas/AdminEntradas.css';

function safeSlug(str) {
  return String(str || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_-]/g, '');
}

function getTsMillis(ts) {
  try {
    if (!ts) return 0;
    if (typeof ts === 'number') return ts;
    if (typeof ts === 'string') {
      const d = new Date(ts);
      return isNaN(d.getTime()) ? 0 : d.getTime();
    }
    if (typeof ts?.toMillis === 'function') return ts.toMillis();
    if (typeof ts?.seconds === 'number') return ts.seconds * 1000;
    const d = new Date(ts);
    return isNaN(d.getTime()) ? 0 : d.getTime();
  } catch {
    return 0;
  }
}

// Un histórico sin ID no se enlaza a una persona por coincidencia de nombre.
const claveAtribucion = (r) => r.atribuidoAUid || 'legacy:' + r.id;

export default function PanelInvitaciones({ soloLectura = false }) {
  const navigate = useNavigate();
  const { eventoId } = useParams();

  const { profile } = useAuth();

  const puedeEliminar = permiteRol(profile,'invitaciones.eliminar');
  const [items, setItems] = useState([]);
  const [fichasValidas, setFichasValidas] = useState([]);
  const [loading, setLoading] = useState(true);

  // Info del evento seleccionado
  const [eventoInfo, setEventoInfo] = useState(null);
  const [loadingEvento, setLoadingEvento] = useState(true);
  useEffect(() => {
    let alive = true;
    if (eventoInfo) obtenerRelacionadoresEvento(eventoInfo, 'invitaciones').then((lista) => { if (alive) setFichasValidas(lista); }).catch(() => { if (alive) setFichasValidas([]); });
    return () => { alive = false; };
  }, [eventoInfo, profile]);


  const puedeEditar=!soloLectura&&permiteRol(profile,'invitaciones.editar')&&puedeAccederEvento(profile,eventoInfo,'reportes');
  const esSoloLectura=soloLectura||!puedeAccederEvento(profile,eventoInfo,'reportes')||(!puedeEditar&&!puedeEliminar);

  // Filtros (SIN filtro de evento, porque ya está fijado por :eventoId)
  const [qText, setQText] = useState('');
  const [estado, setEstado] = useState('todos');
  const [usuarioFiltro, setUsuarioFiltro] = useState('');
  const [atribucionFiltro, setAtribucionFiltro] = useState('todos');

  // Selecciones
  const [selected, setSelected] = useState(new Set());

  // Edición individual
  const [editando, setEditando] = useState(null);

  // Edición masiva
  const [bulkEstado, setBulkEstado] = useState('usada');
  const [atribucionMasivaUid, setAtribucionMasivaUid] = useState('');

  useEffect(() => {
    if (!eventoId) return;
    fetchEvento();
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventoId]);

  async function fetchEvento() {
    setLoadingEvento(true);
    try {
      const ref = doc(db, 'eventos', eventoId);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        setEventoInfo({ id: snap.id, ...snap.data() });
      } else {
        setEventoInfo(null);
      }
    } catch (e) {
      console.error('Error cargando evento:', e);
      setEventoInfo(null);
    } finally {
      setLoadingEvento(false);
    }
  }

  async function fetchAll() {
    setLoading(true);
    setSelected(new Set());

    if (!eventoId) {
      setItems([]);
      setLoading(false);
      return;
    }

    const eventoSnap = await getDoc(doc(db, 'eventos', eventoId));
    const eventoData = eventoSnap.exists() ? eventoSnap.data() : null;
    const filtros = [where('eventoId', '==', eventoId)];
    if (eventoData?.empresaId) filtros.push(where('empresaId', '==', eventoData.empresaId));

    // IMPORTANTE: usamos where sin orderBy para evitar índices extra.
    // Ordenamos en cliente por createdAt desc.
    const qy = query(collection(db, 'invitaciones'), ...filtros);
    const snap = await getDocs(qy);

    const arr = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    arr.sort((a, b) => getTsMillis(b.createdAt) - getTsMillis(a.createdAt));

    setItems(arr);
    setLoading(false);
  }

  // --- Totales (solo del evento, porque items ya viene filtrado) ---
  const totalInvitaciones = items.length;
  const totalUsadas = items.filter(i => i.estado === 'usada').length;
  const totalNoUsadas = items.filter((i) => i.estado === 'no_usada').length;

  // --- Totales filtrados (según filtros actuales) ---
  const { totalFiltrado, usadasFiltrado, noUsadasFiltrado } = useMemo(() => {
    let r = [...items];

    if (estado !== 'todos') {
      r = r.filter(x => (x.estado || 'no_usada') === estado);
    }
    if (usuarioFiltro) {
      r = r.filter(x => x.creadoPorUid === usuarioFiltro);
    }
    if (atribucionFiltro !== 'todos') {
      r = r.filter(x => claveAtribucion(x) === atribucionFiltro);
    }

    const t = qText.trim().toLowerCase();
    if (t) {
      r = r.filter(
        x =>
          (x.nombre || '').toLowerCase().includes(t) ||
          nombreAtribuido(x).toLowerCase().includes(t) ||
          (x.id || '').toLowerCase().includes(t)
      );
    }

    const total = r.length;
    const usadas = r.filter(i => i.estado === 'usada').length;
    const noUsadas = r.filter((i) => i.estado === 'no_usada').length;

    return {
      totalFiltrado: total,
      usadasFiltrado: usadas,
      noUsadasFiltrado: noUsadas,
    };
  }, [items, qText, estado, usuarioFiltro, atribucionFiltro]);

  // Usuarios únicos
  const usuarios = useMemo(() => {
    const map=new Map();
    items.forEach(i=>{if(!i.creadoPorUid)return;const nombre=String(i.creadoPorNombre||'').trim();const old=map.get(i.creadoPorUid);if(!old||!old.nombre&&nombre)map.set(i.creadoPorUid,{id:i.creadoPorUid,nombre:nombre||old?.nombre||''});});
    return [...map.values()].map(u=>({...u,nombre:u.nombre||'Usuario sin nombre'})).sort((a,b)=>a.nombre.localeCompare(b.nombre,'es'));
  }, [items]);

  // Promotores únicos
  const promotores = useMemo(() => {
    const map = new Map();
    items.forEach((r) => map.set(claveAtribucion(r), { id: claveAtribucion(r), nombre: nombreAtribuido(r) }));
    return [...map.values()];
  }, [items]);

  // Filtrado final de filas
  const rows = useMemo(() => {
    let r = items;

    if (estado !== 'todos') {
      r = r.filter(x => (x.estado || 'no_usada') === estado);
    }
    if (usuarioFiltro) {
      r = r.filter(x => x.creadoPorUid === usuarioFiltro);
    }
    if (atribucionFiltro !== 'todos') {
      r = r.filter(x => claveAtribucion(x) === atribucionFiltro);
    }

    const t = qText.trim().toLowerCase();
    if (t) {
      r = r.filter(
        x =>
          (x.nombre || '').toLowerCase().includes(t) ||
          nombreAtribuido(x).toLowerCase().includes(t) ||
          (x.id || '').toLowerCase().includes(t)
      );
    }

    return r;
  }, [items, qText, estado, usuarioFiltro, atribucionFiltro]);

  // Selecciones
  const allSelected = rows.length > 0 && rows.every(r => selected.has(r.id));
  const toggleAll = checked => setSelected(checked ? new Set(rows.map(r => r.id)) : new Set());
  const toggleOne = id =>
    setSelected(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  // Acciones individuales
  const eliminar = async row => {
    if (esSoloLectura || !puedeEliminar) return;
    if (!window.confirm(`¿Eliminar invitación de "${row.nombre}"?`)) return;
    await deleteDoc(doc(db, 'invitaciones', row.id));
    await fetchAll();
  };

  const eliminarSeleccionados = async () => {
    if (esSoloLectura || !puedeEliminar) return;
    if (!selected.size) return;
    if (!window.confirm(`¿Eliminar ${selected.size} invitaciones seleccionadas?`)) return;
    await Promise.all(Array.from(selected).map(id => deleteDoc(doc(db, 'invitaciones', id))));
    await fetchAll();
    setSelected(new Set());
  };

  const marcarUsadas = async () => {
    if (!puedeEditar) return;
    if (!selected.size) return;
    await Promise.all(
      Array.from(selected).map(id => updateDoc(doc(db, 'invitaciones', id), { estado: 'usada' }))
    );
    await fetchAll();
    setSelected(new Set());
  };

  const guardarEdicion = async () => {
    if (!puedeEditar) return;
    if (!editando) return;
    let atribucion = {};
    if (editando.nuevoAtribuidoAUid !== undefined) {
      try {
        atribucion = resolverAtribucion(await obtenerRelacionadoresEvento(eventoInfo, 'invitaciones'), editando.nuevoAtribuidoAUid, eventoInfo, 'invitaciones');
      } catch (e) { window.alert(e.message); return; }
    }
    await updateDoc(doc(db, 'invitaciones', editando.id), {
      ...atribucion,
      nombre: editando.nombre,
      estado: editando.estado,
    });
    setEditando(null);
    await fetchAll();
  };

  // Acciones masivas de edición
  const aplicarBulkEstado = async () => {
    if (!puedeEditar) return;
    if (!selected.size) return;
    if (!bulkEstado) {
      window.alert('Selecciona un estado para aplicar.');
      return;
    }
    await Promise.all(
      Array.from(selected).map(id => updateDoc(doc(db, 'invitaciones', id), { estado: bulkEstado }))
    );
    await fetchAll();
    setSelected(new Set());
  };

  const aplicarAtribucionMasiva = async () => {
    if (!puedeEditar) return;
    if (!selected.size) return;
    const nuevo = atribucionMasivaUid.trim();
    if (!nuevo) {
      window.alert('Selecciona un promotor.');
      return;
    }
    let atribucion;
    try { atribucion = resolverAtribucion(await obtenerRelacionadoresEvento(eventoInfo, 'invitaciones'), nuevo, eventoInfo, 'invitaciones'); }
    catch (e) { window.alert(e.message); return; }
    await Promise.all(Array.from(selected).map(id => updateDoc(doc(db, 'invitaciones', id), atribucion)));
    await fetchAll();
    setSelected(new Set());
  };

  function formatDate(ts) {
    try {
      if (!ts) return '—';
      const millis=getTsMillis(ts);if(!millis)return '—';
      const d = new Date(millis);
      if (isNaN(d.getTime())) return '—';
      return d.toLocaleString('es-BO',{timeZone:'America/La_Paz',day:'2-digit',month:'2-digit',year:'numeric',hour:'2-digit',minute:'2-digit',second:'2-digit',hourCycle:'h23'});
    } catch {
      return '—';
    }
  }

  // Helpers para reportes por evento
  const eventoNombre = eventoInfo?.nombre || (items[0]?.evento || '') || `Evento ${eventoId}`;
  const eventoTag = safeSlug(eventoNombre) || safeSlug(eventoId);

  // Handlers de exportación (SOLO del evento seleccionado)
  const handleExportCSV = () => {if(!permiteRol(profile,'invitaciones.exportar'))return;
    const stats = {
      total: rows.length,
      usadas: rows.filter(i => i.estado === 'usada').length,
      pendientes: rows.filter(i => i.estado === 'no_usada').length,
    };

    const columns = [
      { key: 'id', label: 'ID' },
      { key: 'nombre', label: 'Nombre' },
      { key: 'atribuidoANombre', label: 'Promotor' },
      { key: 'creadoPorNombre', label: 'Creado por' },
      { key: 'estado', label: 'Estado' },
      { key: 'createdAt', label: 'Creada' },
      { key: 'usadaEn', label: 'Ingreso' },
    ];

    exportCSV(
      rows.map(r => ({ ...r, createdAt: formatDate(r.createdAt), usadaEn: formatDate(r.usadaEn) })),
      stats,
      columns,
      `reporte_invitaciones_${eventoTag}.csv`,
      `Invitaciones — ${eventoNombre}`
    );
  };

  const handleExportPDF = () => {if(!permiteRol(profile,'invitaciones.exportar'))return;
    const stats = {
      total: rows.length,
      usadas: rows.filter(i => i.estado === 'usada').length,
      pendientes: rows.filter(i => i.estado === 'no_usada').length,
    };

    const columns = [
      { key: 'id', label: 'ID' },
      { key: 'nombre', label: 'Nombre' },
      { key: 'atribuidoANombre', label: 'Promotor' },
      { key: 'creadoPorNombre', label: 'Creado por' },
      { key: 'estado', label: 'Estado' },
      { key: 'createdAt', label: 'Creada' },
      { key: 'usadaEn', label: 'Ingreso' },
    ];

    exportPDF(
      rows.map(r => ({ ...r, createdAt: formatDate(r.createdAt), usadaEn: formatDate(r.usadaEn) })),
      stats,
      columns,
      `reporte_invitaciones_${eventoTag}.pdf`,
      `Invitaciones — ${eventoNombre}`
    );
  };

  // Si alguien abre la ruta sin eventoId
  if (!eventoId) {
    return (
      <div className="admin-entradas">
        <div className="ae-card">
          <h2 style={{ marginTop: 0 }}>Administrar invitaciones</h2>
          <div className="ae-empty" style={{ marginTop: 10 }}>
            Falta seleccionar un evento.
            <div style={{ marginTop: 10 }}>
              <button
                className="btn btn-primary"
                type="button"
                onClick={() => navigate('/admin/invitaciones')}
              >
                Elegir evento
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-entradas">
      {/* HEADER */}
      <div className="ae-header">
        <div>
          <h2 style={{ marginBottom: 4 }}>Administrar invitaciones</h2>
          <p className="ae-subtitle" style={{ marginTop: 0 }}>
            Panel separado por evento.
            <span style={{ marginLeft: 8, opacity: 0.9 }}>
              Evento:{' '}
              <b>{loadingEvento ? 'Cargando…' : (eventoInfo?.nombre || (items[0]?.evento || '—'))}</b>
              {eventoInfo?.fechaTexto ? <span style={{ marginLeft: 6, opacity: 0.75 }}>({eventoInfo.fechaTexto})</span> : null}
            </span>
          </p>
        </div>

        <div className="ae-header-actions" style={{ display: 'flex', gap: 8 }}>
          <button
            className="btn btn-secondary"
            onClick={() => navigate('/admin/invitaciones')}
            type="button"
          >
            Cambiar evento
          </button>

          <button className="btn btn-secondary" onClick={fetchAll} disabled={loading} type="button">
            Actualizar
          </button>
        </div>
      </div>

      {/* RESUMEN (cards) */}
      <div className="ae-summary">
        <div className="ae-summary-card">
          <span className="ae-summary-label">Total invitaciones</span>
          <span className="ae-summary-value">{totalInvitaciones}</span>
          {totalFiltrado !== totalInvitaciones && (
            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
              Mostrando <b>{totalFiltrado}</b> con los filtros actuales
            </div>
          )}
        </div>

        <div className="ae-summary-card ae-summary-card--warn">
          <span className="ae-summary-label">Usadas</span>
          <span className="ae-summary-value">{totalUsadas}</span>
          {usadasFiltrado !== totalUsadas && (
            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
              En vista: <b>{usadasFiltrado}</b>
            </div>
          )}
        </div>

        <div className="ae-summary-card ae-summary-card--ok">
          <span className="ae-summary-label">No usadas</span>
          <span className="ae-summary-value">{totalNoUsadas}</span>
          {noUsadasFiltrado !== totalNoUsadas && (
            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
              En vista: <b>{noUsadasFiltrado}</b>
            </div>
          )}
        </div>

        <div className="ae-summary-card">
          <span className="ae-summary-label">Evento</span>
          <span className="ae-summary-value" style={{ fontSize: 14, lineHeight: 1.15 }}>
            {loadingEvento ? 'Cargando…' : (eventoInfo?.nombre || (items[0]?.evento || '—'))}
          </span>
          {eventoInfo?.fechaTexto && (
            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
              {eventoInfo.fechaTexto}
            </div>
          )}
        </div>
      </div>

      {/* TOOLBAR: búsqueda + filtros + export */}
      <div className="ae-toolbar">
        {/* BÚSQUEDA */}
        <input
          className="ae-search"
          placeholder="Buscar por nombre, promotor o ID..."
          value={qText}
          onChange={e => setQText(e.target.value)}
        />

        <div className="ae-toolbar-right" style={{ flexWrap: 'wrap' }}>
          {/* FILTROS */}
          <div className="ae-bulk-actions" style={{ flexWrap: 'wrap' }}>
            <select
              aria-label="Filtrar por promotor"
              value={atribucionFiltro}
              onChange={e => setAtribucionFiltro(e.target.value)}
              style={selectDarkStyle}
            >
              <option value="todos">Todos los promotores</option>
              {promotores.map(p => (
                <option key={p.id} value={p.id}>
                  {p.nombre}
                </option>
              ))}
            </select>

            <select
              aria-label="Filtrar por usuario"
              value={usuarioFiltro}
              onChange={e => setUsuarioFiltro(e.target.value)}
              style={selectDarkStyle}
            >
              <option value="">Todos los usuarios</option>
              {usuarios.map(u => (
                <option key={u.id} value={u.id}>
                  {u.nombre}
                </option>
              ))}
            </select>

            <select value={estado} onChange={e => setEstado(e.target.value)} style={selectDarkStyle}>
              <option value="todos">Todos los estados</option>
              {Object.entries(ESTADOS_QR).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>

            <button
              onClick={() => {
                setQText('');
                setEstado('todos');
                setUsuarioFiltro('');
                setAtribucionFiltro('todos');
              }}
              className="btn btn-secondary"
              type="button"
            >
              Limpiar filtros
            </button>
          </div>

          {/* EXPORT */}
          <div className="ae-export">
            <button className="btn btn-ghost" hidden={!permiteRol(profile,'invitaciones.exportar')} onClick={handleExportCSV} type="button">
              CSV
            </button>
            <button className="btn btn-ghost" hidden={!permiteRol(profile,'invitaciones.exportar')} onClick={handleExportPDF} type="button">
              PDF
            </button>
          </div>
        </div>
      </div>

      {/* BARRA DE EDICIÓN MASIVA */}
      {!esSoloLectura && selected.size > 0 && (
        <div
          style={{
            marginBottom: 10,
            padding: '8px 10px',
            borderRadius: 10,
            background: '#0f172a',
            border: '1px solid #334155',
            fontSize: 12,
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 6 }}>
            Edición masiva: {selected.size} invitación(es) seleccionada(s)
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
            {/* Cambiar estado */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
              <span>Estado:</span>
              <select value={bulkEstado} onChange={e => setBulkEstado(e.target.value)} style={selectDarkStyle}>
                <option value="usada">Usada</option>
                <option value="no_usada">No usada</option>
              </select>
              <button onClick={aplicarBulkEstado} className="btn btn-primary" type="button">
                Aplicar a selección
              </button>
            </div>

            {/* Cambiar promotor */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
              <span>Promotor:</span>
              <select
                value={atribucionMasivaUid}
                onChange={e => setAtribucionMasivaUid(e.target.value)}
                style={{ ...selectDarkStyle, minWidth: 180 }}
              >
                <option value="">Selecciona un promotor</option>
                {fichasValidas.map(p => (
                  <option key={p.id} value={p.id}>
                    {etiquetaRelacionador(p)}
                  </option>
                ))}
              </select>
              <button onClick={aplicarAtribucionMasiva} className="btn btn-primary" type="button">
                Aplicar a selección
              </button>
            </div>

            {/* Acciones existentes */}
            <div style={{ marginLeft: 'auto', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              <button onClick={marcarUsadas} className="btn btn-secondary" type="button">
                Marcar usadas
              </button>
              <button disabled={!puedeEliminar} onClick={eliminarSeleccionados} className="btn btn-danger" type="button">
                Eliminar seleccionadas
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TABLA */}
      <div className="ae-table-wrapper">
        <table className="ae-table">
          <thead>
            <tr>
              <th style={{ width: 36, textAlign: 'center' }}>
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={e => toggleAll(e.target.checked)}
                  disabled={rows.length === 0}
                />
              </th>
              <th>Nombre</th>
              <th>Promotor</th>
              <th>Creado por</th>
              <th>Creada</th>
              <th>Ingreso</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>

          <tbody>
            {loading && (
              <tr>
                <td colSpan={8} className="ae-empty">
                  Cargando invitaciones…
                </td>
              </tr>
            )}

            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={8} className="ae-empty">
                  No hay registros que coincidan con los filtros.
                </td>
              </tr>
            )}

            {!loading &&
              rows.map(row => {
                const estadoValor = row.estado || 'no_usada';
                return (
                  <tr key={row.id}>
                    <td style={{ textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={selected.has(row.id)}
                        onChange={() => toggleOne(row.id)}
                        disabled={esSoloLectura}
                      />
                    </td>
                    <td>{row.nombre}</td>
                    <td>{nombreAtribuido(row)}</td>
                    <td>{row.creadoPorNombre || '—'}</td>
                    <td>{formatDate(row.createdAt)}</td>
                    <td>{formatDate(row.usadaEn)}</td>
                    <td>
                      <span
                        className={
                          'ae-badge ' +
                          (estadoValor === 'usada' ? 'ae-badge--used' : 'ae-badge--pending')
                        }
                      >
                        {ESTADOS_QR[estadoValor] || 'Estado desconocido'}
                      </span>
                    </td>
                    <td>
                      {!esSoloLectura ? (
                        <div className="ae-actions">
                          <button
                            disabled={!puedeEditar} onClick={() => setEditando(row)}
                            className="btn btn-link"
                            type="button"
                          >
                            Editar
                          </button>
                          <button
                            disabled={!puedeEliminar}
                            onClick={() => eliminar(row)}
                            className="btn btn-link btn-link--danger"
                            type="button"
                          >
                            Eliminar
                          </button>
                        </div>
                      ) : (
                        <span className="ae-solo-lectura">Solo lectura</span>
                      )}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {/* MODAL EDICIÓN */}
      {editando && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50,
          }}
          onClick={() => setEditando(null)}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#020617',
              padding: 20,
              borderRadius: 10,
              width: '90%',
              maxWidth: 420,
              boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
              border: '1px solid #1f2937',
              color: '#e5e7eb',
            }}
          >
            <h3 style={{ marginTop: 0, marginBottom: 12 }}>Editar invitación</h3>
            <div style={{ display: 'grid', gap: 10 }}>
              <div>
                <label style={labelModal}>Nombre</label>
                <input
                  value={editando.nombre || ''}
                  onChange={e => setEditando({ ...editando, nombre: e.target.value })}
                  style={inputModal}
                />
              </div>
              <div>
                <label style={labelModal}>Promotor</label>
                <select aria-label="Reasignar relacionador" value={editando.nuevoAtribuidoAUid ?? '__mantener'} onChange={e => setEditando({ ...editando, nuevoAtribuidoAUid: e.target.value === '__mantener' ? undefined : e.target.value })} style={inputModal}>
                  <option value="__mantener">Mantener: {nombreAtribuido(editando)}</option>
                  <option value="">Sin promotor / venta directa</option>
                  {fichasValidas.map((p) => <option key={p.id} value={p.id}>{etiquetaRelacionador(p)}</option>)}
                </select>
              </div>
              <div>
                <label style={labelModal}>Estado</label>
                <select
                  value={editando.estado || 'no_usada'}
                  onChange={e => setEditando({ ...editando, estado: e.target.value })}
                  style={inputModal}
                >
                  {Object.entries(ESTADOS_QR).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </div>
            </div>
            <div
              style={{
                marginTop: 18,
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 8,
              }}
            >
              <button onClick={() => setEditando(null)} className="btn btn-secondary" type="button">
                Cancelar
              </button>
              <button onClick={guardarEdicion} className="btn btn-primary" type="button">
                Guardar cambios
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ==== Estilos auxiliares para selects y modal (coherentes con tema oscuro) ==== */
const selectDarkStyle = {
  padding: '6px 10px',
  borderRadius: 999,
  border: '1px solid #374151',
  background: '#020617',
  color: '#e5e7eb',
  fontSize: 12,
  minWidth: 150,
};

const labelModal = {
  display: 'block',
  fontSize: 13,
  fontWeight: 600,
  marginBottom: 4,
};

const inputModal = {
  width: '100%',
  padding: '7px 9px',
  borderRadius: 6,
  border: '1px solid #4b5563',
  background: '#020617',
  color: '#e5e7eb',
  fontSize: 14,
  boxSizing: 'border-box',
};
